import { ColumnDef, RowData, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { HeaderCheckbox } from '../../../../shared/components/HeaderCheckbox';
import { Item } from '../../types/item.types';
import { RowCheckbox } from '../../../../shared/components/RowCheckbox';
import SkapaTable from '@ingka/table';
import { TableBody } from './TableBody';
import { TableHeader } from './TableHeader';
import { TablePagination } from './TablePagination';
import styled from '@emotion/styled';
import { filterEmissionColumnsByRole, tableColumns } from './ColumnDefs';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useAuth } from '../../../auth/state/auth.state';
import { useColumnOrderState } from '../../state/column-order.state';
import { useHiddenColumnState } from '../../state/column-hiding.state';
import { useTableDataState } from "../../state/table-data.state";
import { applyOrder } from '../../../../shared/utils/order';

declare module '@tanstack/table-core' {
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   interface ColumnMeta<TData extends RowData, TValue> {
      width?: string;
      rightAligned?: boolean;
   }
}

export const Table = () => {
   const responseData = useTableDataState((state) => state.data);
   const query = useTableDataState((state) => state.query);
   const { canManageEmissionFactors } = useAuth();
   const tableRef = useRef<HTMLDivElement>(null);

   const hiddenColumns = useHiddenColumnState((s) => s.hiddenColumns);
   const setHiddenColumns = useHiddenColumnState((s) => s.setHiddenColumns);
   const [data, setData] = useState(responseData || []);
   const mainOrder = useColumnOrderState(s => s.orders.main);
   const orderedMain = useMemo(
      () => applyOrder(filterEmissionColumnsByRole(tableColumns.main, canManageEmissionFactors), mainOrder, c => c.accessorKey),
      [canManageEmissionFactors, mainOrder]
   );

   useEffect(() => {
      setData(responseData || []);
   }, [responseData]);

   useEffect(() => {
      const tableEl = tableRef.current?.querySelector('.table--full-width');
      if (tableEl) {
         tableEl.scrollTop = 0;
      }
   }, [query.offset]);

   const columns: ColumnDef<Item>[] = [
      {
         id: 'select',
         enableSorting: false,
         header: () => <HeaderCheckbox />,
         cell: ({ row }) => <RowCheckbox row={row} />,
         meta: {
            width: '5.5rem',
         },
      },
      ...orderedMain,
   ];

   const instance = useReactTable({
      data,
      columns,
      state: {
         columnVisibility: hiddenColumns,
      },
      onColumnVisibilityChange: setHiddenColumns,
      getCoreRowModel: getCoreRowModel(),
   });

   return (
      <>
         <StyledTableDiv ref={tableRef}>
            <StyledTable fullWidth={true}>
               <TableHeader instance={instance} />
               <TableBody instance={instance} onItemsUpdate={setData} />
            </StyledTable>
         </StyledTableDiv>
         <StyledPaginationDiv>
            <TablePagination />
         </StyledPaginationDiv>
      </>
   );
};

const StyledTableDiv = styled.div`
   margin: 0 ${tokens.space['3xl']} 0.5rem;
   z-index: ${tokens.zIndex.base};
   padding-top: ${tokens.space.xs};
   display: flex;
   flex-direction: column;
   flex: 1 1 auto;
   overflow: auto;
`;

const StyledTable = styled(SkapaTable)`
   table {
      table-layout: auto;
   }
`;

const StyledPaginationDiv = styled.div`
   position: sticky;
   bottom: 0;
   left: 0;
   right: 0;
   z-index: ${tokens.zIndex.sticky};
   display: flex;
   align-items: center;
   justify-content: flex-end;
   padding: 0 ${tokens.space.xxs};
   width: 100%;
`;